<template>
  <div>
    <div 
      v-if="(layout && layout !== 'Default') || (isMobile || isTablet) && product.files.length"
      class="aspect-[4/3.6]"
    >
      <ClientOnly>
        <Swiper
          :modules="[SwiperPagination, SwiperNavigation]"
          :pagination="{
            type: 'bullets',
          }"
          :navigation="true"
          :slides-per-view="1"
          class="product-images-swiper relative"
          :class="{
            'hide-swiper-nav': product.files.length <= 1,
            '!pt-24 md:!pt-0': layout && layout !== 'Default'
          }"
        >
          <SwiperSlide
            class="items-center rounded-md xsOnly:px-32 px-64 justify-center aspect-[4/3.6] !flex"
            :class="{
              'bg-grey200': !layout || layout === 'Default'
            }"
          >
            <nuxt-img
              v-if="product.imageUrl"
              provider="norce"
              class="sm:max-w-[500px] md:max-w-[420px] aspect-square sm:ratio-4/3 object-contain"
              :src="product.imageUrl"
              :sizes="product.productType === 'Bundle' ? 'sm:350px md:720px' : '380px sm:600px md:500px'"
              preset="productImage"
              :alt="product.name"
              :class="{
                '!max-w-full w-full !object-cover rounded-md': product.productType === 'Bundle',
              }"
              :width="460"
              :height="460"
            />
          </SwiperSlide>
          <SwiperSlide
            v-for="(image, index) in product.files"
            :key="index"
            class="!flex items-center rounded-md xsOnly:px-32 px-64 justify-center aspect-[4/3.6]"
            :class="{
              'bg-grey200': !layout || layout === 'Default'
            }"
          >
            <nuxt-img
              v-if="image.url"
              provider="norce"
              class="sm:max-w-[500px] md:max-w-[420px] min-w-3/5 aspect-square sm:ratio-4/3 object-contain"
              :src="image.url"
              :sizes="product.productType === 'Bundle' ? 'sm:350px md:720px' : '380px sm:600px md:500px'"
              preset="productImage"
              :alt="product.name"
              :class="{
                '!max-w-full w-full !object-cover rounded-md': product.productType === 'Bundle',
              }"
            />
          </SwiperSlide>
          <div
            v-if="product.category.productListingUrl && flags && flags.length"
            class="absolute top-12 left-12 z-10 flex items-start"
          >
            <div 
              v-for="flag in props.isSubscription ? subscriptionFlags : flags"
              :key="`flag-${flag.name}-${product.id}`"
            >
              <GlobalsLinkHelper
                v-if="flag.groupName == 'E-com Members Flags'"
                :to="`${product.category.productListingUrl}${flag.urlSegment}/`"
                class="block leading-tiny px-12 pb-4 pt-6 mr-8 last:mr-0 mb-12 text-xsmall-medium text-white w-fit-content bg-primary rounded-full animate-all hover:border-grey800"
              >
                <img
                  :src="'/heroicons/outline/star-white.svg'"
                  alt="stjärna"
                  class="inline-block mb-4"
                  height="12"
                  width="12"
                >
                {{ flag.name }}
              </GlobalsLinkHelper>
              <GlobalsLinkHelper
                v-else
                :to="`${product.category.productListingUrl}${flag.urlSegment}/`"
                class="block leading-tiny px-12 pb-4 pt-6 mr-8 last:mr-0 mb-12 text-xsmall-medium w-fit-content bg-white rounded-full border border-grey400 animate-all hover:border-grey800"
              >
                {{ flag.name }}
              </GlobalsLinkHelper>
            </div> 
          </div>
        </Swiper>
      </ClientOnly>
    </div>
    <div v-else class="flex">
      <div v-if="product.files.length" class="flex">
        <div>
          <div
            class="mb-12 w-96 h-96 mr-24 flex-shrink-0"
          >
            <div
              class="product-image-small h-full w-full overflow-hidden bg-grey200 cursor-pointer relative rounded-md"
              :class="{
                'border-grey800 border-2': activeMediaIndex === -1
              }"
              @click="(activeMedia = product.imageUrl), (activeMediaIndex = -1)"
            >
              <div
                class="absolute left-0 w-full h-full top-0 flex justify-center items-center p-12"
              >
                <nuxt-img
                  provider="norce"
                  :src="product.imageUrl"
                  class="z-10 object-scale-down h-full w-full"
                  :alt="product.name"
                  preset="productImage"
                  height="90"
                  width="90"
                />
              </div>
            </div>
          </div>
          <div
            v-for="(media, idx) in product.files"
            :key="'productImageNonSwiper-' + idx + media.url"
            class="mb-12 w-96 h-96 mr-24 flex-shrink-0"
          >
            <div
              v-if="media.url"
              class="product-image-small h-full w-full overflow-hidden bg-grey200 cursor-pointer relative rounded-md border border-grey400"
              :class="{
                'border-grey800 border-2': idx === activeMediaIndex
              }"
              @click="(activeMedia = media.url), (activeMediaIndex = idx)"
            >
              <div
                class="absolute left-0 w-full h-full top-0 flex justify-center items-center p-12"
              >
                <nuxt-img
                  provider="norce"
                  :src="media.url"
                  class="z-10 object-scale-down h-full w-full"
                  :alt="product.name"
                  preset="productImage"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="relative w-full">
        <div
          class="flex items-center rounded-md px-64 justify-center aspect-[4/3.6]"
          :class="{
            '!px-0': product.productType === 'Bundle',
            'bg-grey200': !layout || layout === 'Default' 
          }"
        >
          <nuxt-img
            v-if="product.imageUrl"
            provider="norce"
            :src="activeMedia ? activeMedia : product.imageUrl"
            preset="productImage"
            :sizes="product.productType === 'Bundle' ? 'sm:350px md:720px' : '380px sm:600px md:500px'"
            class="w-full h-full object-contain max-w-[380px] sm:max-w-[500px] md:max-w-[420px] aspect-square"
            :class="{
              '!max-w-full w-full !object-cover rounded-md': product.productType === 'Bundle',
            }"
            :alt="product.name"
            :width="activeMediaIndex >= 0 ? '' : 400"
            :height="activeMediaIndex >= 0 ? '' : 400"
          />
          <div
            v-if="product.category.productListingUrl"
            class="absolute top-12 left-12 z-20 flex items-start"
          >
            <div 
              v-for="flag in props.isSubscription ? subscriptionFlags : flags"
              :key="`flag-${flag.name}-${product.id}`"
            >
              <GlobalsLinkHelper
                v-if="flag.groupName == 'E-com Members Flags'"
                :to="`${product.category.productListingUrl}${flag.urlSegment}/`"
                class="block leading-tiny px-12 pb-4 pt-6 mr-8 mb-12 text-xsmall-medium text-white w-fit-content bg-primary rounded-full animate-all hover:border-grey800"
              >
                <img
                  :src="'/heroicons/outline/star-white.svg'"
                  alt="stjärna"
                  class="inline-block mb-4"
                  height="12"
                  width="12"
                >
                {{ flag.name }}
              </GlobalsLinkHelper>
              <GlobalsLinkHelper
                v-else
                :to="`${product.category.productListingUrl}${flag.urlSegment}/`"
                class="block leading-tiny px-12 pb-4 pt-6 mr-8 mb-12 text-xsmall-medium w-fit-content bg-white rounded-full border border-grey400 animate-all hover:border-grey800"
              >
                {{ flag.name }}
              </GlobalsLinkHelper>
            </div>          
          </div>
        </div>
        <RelatedProducts
          v-if="page && page.relatedProducts && page.relatedProducts.length && product.productType !== 'Bundle'"
          class="mt-24"
          :product="product"
          :page="page"
        />
      </div>
    </div>
  </div>
</template>
  
<script setup lang="ts">
import type { IProduct, IFlag, ITag, IProductPageData } from '~/api-types';
import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';
import 'swiper/css';
import 'swiper/css/pagination';
import RelatedProducts from '~/components/product-page/RelatedProducts.vue';
const uiStore = useUiStore();
const { isMobile, isTablet } = storeToRefs(uiStore);

const props = defineProps<{
  product: IProduct;
  modelDescription?: string;
  isSubscription?: boolean,
  flagsToHide?: string,
  page: IProductPageData,
  layout?: string
}>();

const activeMediaIndex = ref(-1);
const activeMedia = ref(props.product.imageUrl);

const flags = computed<IFlag[] | null>(() => {
  return props.product?.flags
    .filter((item: IFlag) =>
      props.product.tags.some((tag: ITag) => tag.urlSegment === item.urlSegment)
    )
    .sort((a, b) => (a.groupName === 'E-com Members Flags' ? -1 : b.groupName === 'E-com Members Flags' ? 1 : 0));
});

const subscriptionFlags = computed<IFlag[] | null>(() => {
  return props.product?.flags
    .filter((item : IFlag) => {
      return props.product?.tags.some((tag : ITag) => tag.urlSegment === item.urlSegment && !props.flagsToHide?.includes(item.urlSegment));
    })
    .sort((a, b) => (a.groupName === 'E-com Members Flags' ? -1 : b.groupName === 'E-com Members Flags' ? 1 : 0));
});
  
</script>

<style>
.product-images-swiper {
  .swiper-button-next {
    @apply right-0;
  }

  .swiper-button-disabled {
    @apply hidden;
  }

  &.hide-swiper-nav {
    .swiper-button-next,
    .swiper-button-prev {
      @apply hidden;
    }
  }
}
</style>
